import { useMutation } from "@tanstack/react-query";
import { Download, FileSpreadsheet, Loader2, UploadCloud, X } from "lucide-react";
import React from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";

import { CustomFetchError } from "@/client/custom-fetch";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components";
import { toast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

import {
  getCampaignContactsTemplate,
  uploadCampaignContacts,
} from "../../campaign-upload.api";
import { TCampaignContactsTemplate } from "../../campaign-upload.types";

export interface ICampaignUploadLeadsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  onSuccess?: () => void;
}

export const CampaignUploadLeadsModal: React.FC<
  ICampaignUploadLeadsModalProps
> = ({ open, onOpenChange, campaignId, onSuccess }) => {
  const { t } = useI18n();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadCampaignContacts(campaignId, file),
    onSuccess: () => {
      toast({
        title: t("generic.success"),
        description: t("campaigns.upload.success.processed"),
      });
      setSelectedFile(null);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      let message = t("campaigns.upload.errors.generic");
      if (error instanceof CustomFetchError) {
        if (error.status === 404) {
          message = t("campaigns.upload.errors.campaignNotFound");
        } else if (error.status === 400 && error.message) {
          message = error.message;
        }
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      toast({
        title: t("generic.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: (): Promise<TCampaignContactsTemplate> =>
      getCampaignContactsTemplate(),
    onSuccess: (template) => {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([
        template.headers,
        template.sample_row,
      ]);
      XLSX.utils.book_append_sheet(workbook, worksheet, template.sheet_name);
      XLSX.writeFile(workbook, "leads-template.xlsx");
      toast({
        title: t("generic.success"),
        description: t("campaigns.upload.modal.templateDownloaded"),
      });
    },
    onError: () => {
      toast({
        title: t("generic.error"),
        description: t("campaigns.upload.errors.templateDownload"),
        variant: "destructive",
      });
    },
  });

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const [first] = acceptedFiles;
    setSelectedFile(first ?? null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploadMutation.isPending,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".csv"],
    },
  });

  const handleSelectFromComputer = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "csv" || ext === "xlsx") {
        setSelectedFile(file);
      } else {
        toast({
          title: t("generic.error"),
          description: t("campaigns.upload.validation.invalidExtension"),
          variant: "destructive",
        });
      }
    }
    e.target.value = "";
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      toast({
        title: t("generic.error"),
        description: t("campaigns.upload.modal.selectFileFirst"),
        variant: "destructive",
      });
      return;
    }
    uploadMutation.mutate(selectedFile);
  };

  const handleClose = () => {
    if (!uploadMutation.isPending) {
      setSelectedFile(null);
      onOpenChange(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("campaigns.upload.modal.title")}</DialogTitle>
          <DialogDescription>
            {t("campaigns.upload.modal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">
              {t("campaigns.upload.modal.templateLabel")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplateMutation.mutate()}
              disabled={downloadTemplateMutation.isPending}
              className="w-fit"
            >
              {downloadTemplateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t("campaigns.upload.form.downloadTemplate")}
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">
              {t("campaigns.upload.modal.uploadLabel")}
            </p>
            {selectedFile ? (
              <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <FileSpreadsheet className="h-6 w-6 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    disabled={uploadMutation.isPending}
                    className="shrink-0"
                    aria-label={t("generic.close")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("campaigns.upload.modal.fileLoaded")}
                </p>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={cn(
                  "rounded-lg border-2 border-dashed p-6 text-center transition-colors cursor-pointer",
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/60",
                  uploadMutation.isPending && "opacity-60 cursor-not-allowed"
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="h-8 w-8 text-primary" />
                  <p className="text-sm font-medium">
                    {t("campaigns.upload.form.title")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("campaigns.upload.form.description")}
                  </p>
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={handleFileInputChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectFromComputer}
              disabled={uploadMutation.isPending}
              className="w-full"
            >
              {t("campaigns.upload.modal.selectFromComputer")}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
          >
            {t("generic.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedFile || uploadMutation.isPending}
          >
            {uploadMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {uploadMutation.isPending
              ? t("campaigns.upload.processing")
              : t("campaigns.upload.form.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
